/** Simple history stack used by the board editor to support undo/redo */
export class HistoryStack<T> {
  private past: T[]
  private future: T[]
  private current: T

  constructor(
    initial: T,
    private cloneFn: (value: T) => T,
    private readonly maxEntries = 50
  ) {
    this.cloneFn = cloneFn
    this.current = this.cloneFn(initial)
    this.past = []
    this.future = []
  }

  push(state: T) {
    this.past.push(this.cloneFn(this.current))
    if (this.past.length > this.maxEntries) {
      this.past.shift()
    }

    this.current = this.cloneFn(state)
    this.future = []
  }

  undo(): T | null {
    if (!this.canUndo) {
      return null
    }

    this.future.unshift(this.cloneFn(this.current))
    const previous = this.past.pop()

    if (!previous) {
      return null
    }

    this.current = this.cloneFn(previous)
    return this.cloneFn(this.current)
  }

  redo(): T | null {
    if (!this.canRedo) {
      return null
    }

    this.past.push(this.cloneFn(this.current))
    const next = this.future.shift()

    if (!next) {
      return null
    }

    this.current = this.cloneFn(next)
    return this.cloneFn(this.current)
  }

  reset(state: T) {
    this.past = []
    this.future = []
    this.current = this.cloneFn(state)
  }

  get canUndo(): boolean {
    return this.past.length > 0
  }

  get canRedo(): boolean {
    return this.future.length > 0
  }

  get snapshot(): T {
    return this.cloneFn(this.current)
  }
}
